import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Monitor } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  getGuardActivity,
  getGuardDevices,
  getPmUser,
  invalidateGuardDevice,
} from '@/services/api.client';
import type { PmUserStatus } from '@/types';

const STATUS_VARIANTS: Record<PmUserStatus, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  suspended: 'outline',
  pending: 'secondary',
};

const ENTRY_METHOD_LABELS: Record<string, string> = {
  qr: 'QR',
  pin: 'PIN',
  manual: 'Manual',
};

interface Props {
  guardId: string;
  propertyId: string;
  isPm: boolean;
  onClose: () => void;
}

export function GuardDetailSheet({ guardId, propertyId, isPm, onClose }: Props) {
  const qc = useQueryClient();

  const guardQuery = useQuery({
    queryKey: ['pm', 'guard-detail', propertyId, guardId],
    queryFn: () => getPmUser(propertyId, guardId),
    enabled: !!guardId,
  });

  const activityQuery = useQuery({
    queryKey: ['pm', 'guard-activity', propertyId, guardId],
    queryFn: () => getGuardActivity(propertyId, { guardUserId: guardId }),
    enabled: !!guardId,
  });

  const deviceQuery = useQuery({
    queryKey: ['pm', 'guard-devices', propertyId, guardId],
    queryFn: () => getGuardDevices(propertyId, guardId),
    enabled: !!guardId && isPm,
  });

  const invalidateMutation = useMutation({
    mutationFn: (deviceId: string) => invalidateGuardDevice(propertyId, deviceId),
    onSuccess: () => {
      toast.success('Session invalidated');
      void qc.invalidateQueries({ queryKey: ['pm', 'guard-devices', propertyId, guardId] });
    },
    onError: () => {
      toast.error('Could not invalidate session');
    },
  });

  const guard = guardQuery.data;
  const activities = activityQuery.data ?? [];
  const device = (deviceQuery.data ?? [])[0] ?? null;

  return (
    <Sheet
      open={!!guardId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader className="pb-4">
          {guardQuery.isLoading ? (
            <Skeleton className="h-6 w-48" />
          ) : guardQuery.isError ? (
            <p className="text-sm text-destructive">Could not load guard details</p>
          ) : guard ? (
            <div className="flex flex-wrap items-center gap-2">
              <SheetTitle>{guard.fullName}</SheetTitle>
              <Badge variant="secondary" className="text-xs">
                {guard.role}
              </Badge>
              <Badge variant={STATUS_VARIANTS[guard.status]} className="text-xs">
                {guard.status}
              </Badge>
            </div>
          ) : null}
          <SheetDescription className="sr-only">
            Guard profile, activity log and session details
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="activity">
          <TabsList className="w-full">
            <TabsTrigger value="activity" className="flex-1">
              Activity
            </TabsTrigger>
            {isPm && (
              <TabsTrigger value="session" className="flex-1">
                Session
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="activity" className="mt-4 space-y-2">
            {activityQuery.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)
            ) : activityQuery.isError ? (
              <p className="text-sm text-destructive">Could not load activity</p>
            ) : activities.length === 0 ? (
              <p className="text-sm text-[var(--text-tertiary)] text-center py-8">
                No activity recorded
              </p>
            ) : (
              activities.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-3 rounded-md border p-2 text-sm"
                >
                  <span className="text-xs font-medium text-[var(--text-tertiary)] pt-0.5 w-12 shrink-0">
                    {ENTRY_METHOD_LABELS[event.entryMethod] ?? event.entryMethod}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{event.visitorName}</p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      {event.gateName ?? 'Unknown gate'} &middot;{' '}
                      {new Date(event.checkedInAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {isPm && (
            <TabsContent value="session" className="mt-4 space-y-4">
              {deviceQuery.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : device ? (
                <div className="rounded-md border p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-[var(--text-tertiary)]" />
                    <span className="text-sm font-medium">
                      {device.deviceName ?? 'Unknown device'} ({device.platform ?? 'unknown'})
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    Last seen:{' '}
                    {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : 'Never'}
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        Invalidate Session
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Invalidate this session?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The guard will be signed out on their device immediately.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => invalidateMutation.mutate(device.id)}
                          disabled={invalidateMutation.isPending}
                        >
                          Invalidate
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : (
                <p className="text-sm text-[var(--text-tertiary)] text-center py-8">
                  No active device session
                </p>
              )}
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
